import { Component, Injector, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * LandRegistryTenure Widget
 */
@Component({
  selector: 'land-registry-tenure-widget',
  templateUrl: './land-registry-tenure-widget.component.html',
  styleUrls: ['./land-registry-tenure-widget.component.scss', '../../common/css/general.scss', '../../common/css/text-and-list.scss']
})
export class LandRegistryTenureWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Add row subject
   */
  private addRowSubject = new Subject<any>();

  /**
   * Add row observable
   */
  public addRow$ = this.addRowSubject.asObservable();

  /**
   * Move current row up subject
   */
  private moveCurrentRowUpSubject = new Subject<void>();

  /**
   * Move current row up observable
   */
  public moveCurrentRowUp$ = this.moveCurrentRowUpSubject.asObservable();

  /**
   * Move current row down subject
   */
  private moveCurrentRowDownSubject = new Subject<void>();

  /**
   * Move current row down observable
   */
  public moveCurrentRowDown$ = this.moveCurrentRowDownSubject.asObservable();

  /**
   * Delete current row subject
   */
  private deleteCurrentRowSubject = new Subject<void>();

  /**
   * Delete current row observable
   */
  public deleteCurrentRow$ = this.deleteCurrentRowSubject.asObservable();

  /**
   * Text & List section rows
   */
  rows: any = [];

  /**
   * Text & List section name
   */
  readonly textAndListSectionName: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'landRegistryTenure', false);
    this.textAndListSectionName = `${this.name}`;//should be the same as defined in repository
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler('landRegistryTenure', this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.initializeLandRegistryTenureSection();
    this.mockupData$ = this.widgetService.loadMockup('landRegistryTenure').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('landRegistryTenure', this.getReport()).pipe();

  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Delete current row event handler
   */
  public deleteCurrentRow(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.deleteCurrentRowSubject.next();
  }

  /**
   * Add row event handler
   */
  public addRow(): void {
    const unselectedFields = this.rows.filter(item => !item.selected);
    if (unselectedFields.length > 0) {
      this.setTextAndListSettings(this.textAndListSectionName, this.rows);
      this.addRowSubject.next({
        topicId: this.getTopicId(),
        fieldName: null,
        isNumericField: false,
        isTextField: true,
        lastTopicId: this.getTopicId(),
        selected: true
      });
    }
  }

  /**
   * Move current row up event handler
   */
  public moveCurrentRowUp(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.moveCurrentRowUpSubject.next();
  }

  /**
   * Move current row down event handler
   */
  public moveCurrentRowDown(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.moveCurrentRowDownSubject.next();
  }

  /**
   * Data changed event handler
   */
  public onDataChanged(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.setDirtyFlag(true);
  }

  /**
   * Initializes the Text & List section
   */
  private initializeLandRegistryTenureSection() {
    this.rows = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionName, [
      {
        topicId: 1,
        fieldName: 'localAuthority',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 2,
        fieldName: 'section',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 3,
        fieldName: 'parcel',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 4,
        fieldName: 'tenure',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 5,
        fieldName: 'nameOfFreeholder',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 6,
        fieldName: 'nameOfLeaseholder',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 7,
        fieldName: 'annualPayment',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 8,
        fieldName: 'durationLeftStartEndDate',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 9,
        fieldName: 'indexation',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 10,
        fieldName: 'renewalRights',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 11,
        fieldName: 'excerptDate',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 12,
        fieldName: 'landRegisterFolioNo',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      }
    ]);

    this.loadReflist('ref_l3_land_registry_tenure');
  }

  /**
   * Get topic Id
   */
  private getTopicId() {
    const unselectedFields = this.rows.filter(item => !item.selected);
    return Math.min(...unselectedFields.map(item => item.topicId));
  }

}
